import { EMPTY, Subject } from 'rxjs';
import { bufferTime, map, mergeMap, takeUntil } from 'rxjs/operators';
import { streem } from 'streem-sdk-protobuf';
import { logger } from '../../util/logger';
import { invariant } from '@streem/toolbox';
import { ungzip } from 'pako';
export class DataController {
    constructor(localSource, remoteSource, localUserId) {
        this.messagesSubject = new Subject();
        this.unsubscribe = new Subject();
        this.incrementLogOfTracksSent = new Subject();
        this.packetAccumulators = {};
        this.messages = this.messagesSubject.asObservable();
        const remoteSourceMessages = remoteSource.pipe(mergeMap(track => (!track ? EMPTY : track.messages)));
        // Log how many data track messages are sent every 5 seconds
        this.bufferCountDataTrackMessages(this.incrementLogOfTracksSent.asObservable()).subscribe(count => {
            if (count > 0) {
                logger.info(`${localUserId} has sent ${count} DataTrackMessages in the past 5 seconds`);
            }
        });
        // Log how many data track messages are received every 5 seconds
        this.bufferCountDataTrackMessages(remoteSourceMessages).subscribe(count => {
            if (count > 0) {
                logger.info(`${localUserId} has received ${count} DataTrackMessages in the past 5 seconds`);
            }
        });
        // Set the local track
        localSource.pipe(takeUntil(this.unsubscribe)).subscribe(track => (this.localTrack = track));
        // Handle remote data track messages
        remoteSourceMessages
            .pipe(takeUntil(this.unsubscribe))
            .subscribe((msg) => {
            if (msg.type !== 'dataTrackPacket') {
                this.messagesSubject.next(msg);
            }
            else {
                this.handleDataTrackPacket(msg);
            }
        });
    }
    disconnect() {
        this.unsubscribe.next(undefined);
    }
    send(message) {
        if (!this.localTrack) {
            logger.warn('Unable to send data track message, no local track exists');
            return;
        }
        this.localTrack.send(message);
        this.incrementLogOfTracksSent.next(undefined);
    }
    bufferCountDataTrackMessages(source) {
        return source.pipe(bufferTime(5000), map(m => m.length), takeUntil(this.unsubscribe));
    }
    handleDataTrackPacket(msg) {
        // if I receive a data chunk, I need to accumulate it somewhere and return early
        // if I receive a data chunk and it's the last chunk needed to construct a message, emit it with this.messagesSubject.next(msg)
        invariant(msg.dataTrackPacket, 'The DataTrackMessage must be a dataTrackPacket');
        const packet = msg.dataTrackPacket;
        invariant(packet.type, 'The type of DataTrackMessage must be defined');
        const accumulatorKey = packet.type;
        //* * If this is the first packet out of many packets and it's not on our accumulators, create it. */
        let accumulator = this.packetAccumulators[accumulatorKey];
        if (packet.index === 1) {
            if (accumulator !== undefined) {
                logger.warn(`Incomplete receipt of DataTrackMessage: ${packet.type} messageId: ${accumulator.id} - recieved ${accumulator.latestIndex} packets. Now recieving packet #${packet.index} of messageId ${packet.messageId}.`);
            }
            accumulator = this.packetAccumulators[accumulatorKey] = {
                id: packet.messageId,
                latestIndex: 0,
                data: [],
            };
        }
        if (accumulator === undefined) {
            logger.warn(`Incomplete receipt of DataTrackMessage: ${packet.type}  - no accumulator found. Now recieving packet #${packet.index} of messageId ${packet.messageId}.`);
            return;
        }
        // If the message is of the same type && we received the next packet...
        if (accumulator.id === packet.messageId && accumulator.latestIndex + 1 === packet.index) {
            accumulator.latestIndex = packet.index;
            if (packet.data) {
                accumulator.data.push(...packet.data);
            }
            // If this is the last packet...
            if (packet.index === packet.numberOfPackets) {
                let data = new Uint8Array(accumulator.data);
                if (packet.compression === streem.room.DataTrackPacket.Compression.COMPRESSION_GZIP) {
                    data = ungzip(data);
                }
                delete this.packetAccumulators[accumulatorKey];
                this.messagesSubject.next(streem.room.DataTrackMessage.decode(new Uint8Array(data)));
            }
        }
        else {
            logger.warn(`Incomplete receipt of DataTrackMessage: ${packet.type} messageId: ${accumulator.id} - recieved ${accumulator.latestIndex} packets. Now recieving packet #${packet.index} of messageId ${packet.messageId}.`);
        }
    }
}
//# sourceMappingURL=data_controller.js.map